
import { takeUntil, mergeMap, distinct, filter } from 'rxjs/operators'
import { combineEpics } from 'redux-observable'
import * as moment from 'moment'
import * as diff from 'lodash/difference'
import * as sumBy from 'lodash/sumBy'
import {
  GET_ACCOUNT_RATES,
  GET_MEMBER_ACCOUNT_BREAKDOWN,
  GET_MEMBER_ACCOUNT_TRENDS,
  GET_MEMBER_ALL_ACCOUNTS,
  GET_PEER_GROUP_ACCOUNT_BREAKDOWN,
  GET_PEER_GROUP_ACCOUNT_TRENDS,
  getMemberAllAccountsActionCreator,
  SET_ACCOUNT_RATES,
  setAccountRatesActionCreator,
  setMemberAccountBreakdownActionCreator,
  setMemberAccountTrendsActionCreator,
  setPeergroupAccountBreakdownActionCreator,
  setPeergroupAccountTrendsActionCreator,
} from 'actions/accountsActions'
import { EMPTY_COLLECTION, getItemsFromCollection, normalize } from 'helpers/normalizeHelpers'
import { of, empty, combineLatest } from 'rxjs'

import {
  GET_ASSETS_RATES_URL,
  GET_DEBTS_RATES_URL,
  GET_MEMBER_ASSETS_ACCOUNTS_ALL_URL,
  GET_MEMBER_ASSETS_BREAKDOWN_URL,
  GET_MEMBER_ASSETS_TREND_URL,
  GET_MEMBER_DEBTS_ACCOUNTS_ALL_URL,
  GET_MEMBER_DEBTS_BREAKDOWN_URL,
  GET_MEMBER_DEBTS_TREND_URL,
  GET_PEER_GROUP_ASSETS_BREAKDOWN_URL,
  GET_PEER_GROUP_ASSETS_TREND_URL,
  GET_PEER_GROUP_DEBTS_BREAKDOWN_URL,
  GET_PEER_GROUP_DEBTS_TREND_URL,
} from 'interfaces/ajax'
import { Epic } from 'interfaces/middleware'
import { DATE_FORMAT_ISO_DAYLESS } from 'constants/datetime'
import { createTimeperiod } from 'helpers/dateTimeHelpers'
import { AjaxResponse } from 'interfaces'
import { TimeScale } from 'interfaces/ajax/base'
import {
  AccountCollection,
  AccountRate,
  AccountType,
  AggregatedBalanceByCategory,
  AllDetailedAccountsDict,
  DetailedAccount,
  RawDetailedAccount,
} from 'interfaces/accounts'
import { getMetaCategoriesByType, resolveCategory } from 'helpers/categoriesHelpers'

import { CollectionCategory, MetaData } from 'interfaces/meta'
import { captureWarning } from 'helpers/errorHelpers'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { PeergroupAccountsBreakdownCollection } from 'interfaces/peergroups'
import {
  createAccountBreakdownDiscardTriggers,
  createAccountRatesDiscardTriggers,
  createAccountsDiscardTriggers,
  createAccountTrendDiscardTriggers,
  PEERGROUPS_DATA_DISCARD_TRIGGERS,
} from 'helpers/discardTriggerHelpers'
import { allAccountsSelector, ratesSelector } from 'selectors/accountSelectors'
import { metaSelector } from 'selectors/globalSelectors'
import { get } from 'helpers/observableAjaxHelpers'
import { PAGE_CHANGED } from 'actions/globalActions'
import { byType } from 'rxOperators/byType'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { end } from 'rxOperators/end'
import { mapSafe, mergeMapSafe } from 'rxOperators/mergeMapSafe'
import { toDiscardCache } from 'rxOperators/toDiscardCache'
import { accountsFilterQueryParamSelector } from 'components/features/pageFilters/selectors'

function processBreakdown (breakdown: AggregatedBalanceByCategory[], meta: MetaData, type: AccountType): AccountCollection {
  const allCategoryBreakdown = {
    category: ALL_CATEGORY_ID,
    amount: sumBy(breakdown, 'amount'),
  }

  const { allKeys: categoryKeys } = getMetaCategoriesByType(type, meta) as CollectionCategory
  const {
    allKeys: breakdownKeys,
    byKey: breakdownByKey,
  } = normalize([allCategoryBreakdown, ...breakdown], 'category')

  categoryKeys.forEach((categoryKey) => {
    if (!breakdownByKey[categoryKey]) {
      breakdownByKey[categoryKey] = {
        category: categoryKey,
        amount: 0,
      }
      breakdownKeys.push(categoryKey)
    }
  })

  return {
    allKeys: breakdownKeys,
    byKey: breakdownByKey,
  }
}

const saturateDetailedAccounts = (accounts: RawDetailedAccount[], categories: CollectionCategory): DetailedAccount[] =>
  accounts.map((account) => ({ ...account, category: categories.byKey[account.category] }))

const createGetMemberAccountsAllEpic = (type: AccountType): Epic =>
  (action$, store) =>
    action$
      .pipe(filterRequestOfType(GET_MEMBER_ALL_ACCOUNTS, 'request')).pipe(
        filter((a) => a.payload.params.type === type),
        distinct(
          () => true,
          action$.pipe(toDiscardCache(createAccountsDiscardTriggers(type))),
        ),
        mergeMap((a) => {
          const isDebts = type === 'debts'
          const state = store.getState()
          const params = {
            excluded_account_uids: accountsFilterQueryParamSelector(state),
          }
          const request = isDebts ? get(GET_MEMBER_DEBTS_ACCOUNTS_ALL_URL, params) : get(GET_MEMBER_ASSETS_ACCOUNTS_ALL_URL, params)

          return request
            .pipe(mergeMapSafe(({ response }) => {
              const meta = metaSelector(store.getState())
              const categories = meta[isDebts ? 'debt_categories' : 'asset_categories']
              const { provided, manual } = response
              const successPayload: AllDetailedAccountsDict = {
                provided: normalize(saturateDetailedAccounts(provided, categories), 'id'),
                manual: normalize(saturateDetailedAccounts(manual, categories), 'id'),
              }

              return of(getMemberAllAccountsActionCreator.success(a.payload.params, successPayload))
            }))
        }))

// ===========================================================================//
//                                 BREAKDOWNS                                 //
// ===========================================================================//

const createGetMemberAccountsBreakdownEpic = (type: AccountType): Epic =>
  (action$, store) =>
    action$
      .pipe(byType(GET_MEMBER_ACCOUNT_BREAKDOWN)).pipe(
        filter(({ payload }) => payload.type === type),
        distinct(
          ({ payload }) => payload.date,
          action$.pipe(toDiscardCache(createAccountBreakdownDiscardTriggers(type))),
        ),
        mergeMap(({ payload }) => {
          const state = store.getState()
          const { meta } = state.data
          const { date } = payload

          const requestPayload = {
            date,
            excluded_account_uids: accountsFilterQueryParamSelector(state),
          }
          const request = type === 'assets'
            ? get(GET_MEMBER_ASSETS_BREAKDOWN_URL, requestPayload)
            : get(GET_MEMBER_DEBTS_BREAKDOWN_URL, requestPayload)

          return request.pipe(
            takeUntil(action$.pipe(byType(PAGE_CHANGED))))
            .pipe(mapSafe(({ response }: AjaxResponse<AggregatedBalanceByCategory[]>) =>
              setMemberAccountBreakdownActionCreator(type, date, processBreakdown(response, meta, type)),
            ))
        },
        ))

const createGetPeergroupAccountsBreakdownEpic = (type: AccountType): Epic =>
  (action$, store) =>
    action$
      .pipe(byType(GET_PEER_GROUP_ACCOUNT_BREAKDOWN)).pipe(
        filter(({ payload }) => payload.type === type),
        distinct(
          ({ payload }) => JSON.stringify(payload),
          action$.pipe(toDiscardCache(PEERGROUPS_DATA_DISCARD_TRIGGERS)),
        ),
        mergeMap(({ payload }) => {
          const { date, peergroups } = payload
          const state = store.getState()
          const { meta } = state.data

          const cachedPeergroupIds: PeergroupAccountsBreakdownCollection = state.data.peergroups[type].breakdown[date] || EMPTY_COLLECTION
          const notCachedPeergroupIds = diff(peergroups, cachedPeergroupIds.allKeys)

          if (notCachedPeergroupIds.length === 0) {
            return empty()
          }

          const params = { group: notCachedPeergroupIds, date }
          const request = type === 'assets'
            ? get(GET_PEER_GROUP_ASSETS_BREAKDOWN_URL, params)
            : get(GET_PEER_GROUP_DEBTS_BREAKDOWN_URL, params)

          return request.pipe(
            takeUntil(action$.pipe(byType(PAGE_CHANGED))))
            .pipe(mapSafe(({ response }) => {
              const normalizedInternals = response.map(({ id, breakdown }) => ({
                id,
                breakdown: processBreakdown(breakdown, meta, type),
              }))
              return setPeergroupAccountBreakdownActionCreator(type, date, normalize(normalizedInternals, 'id'))
            }))
        }))


// ===========================================================================//
//                                   RATES                                    //
// ===========================================================================//

const createGetAccountRatesEpic = (type: AccountType): Epic =>
  (action$, store) =>
    action$
      .pipe(byType(GET_ACCOUNT_RATES)).pipe(
        distinct(
          ({ payload }) => JSON.stringify(payload),
          action$.pipe(toDiscardCache(createAccountRatesDiscardTriggers(type))),
        ),
        filter(({ payload }) => payload.type === type),
        mergeMap(({ payload }) => {
          const { timeframe, peersId } = payload
          const state = store.getState()
          const endpointPayload = {
            ...createTimeperiod(timeframe),
            group: peersId,
            excluded_account_uids: accountsFilterQueryParamSelector(state),
          }

          const request = type === 'assets'
            ? get(GET_ASSETS_RATES_URL, endpointPayload)
            : get(GET_DEBTS_RATES_URL, endpointPayload)

          return request
            .pipe(mapSafe(({ response }) =>
              setAccountRatesActionCreator(normalize(response, 'id'), type, timeframe),
            ))
        },
        ))

const catchMissedRateAccountEpic = (type: AccountType): Epic =>
  (action$, store) => {
    const rates$ = action$
      .pipe(byType(SET_ACCOUNT_RATES)).pipe(
        filter((a) => a.payload.type === type))

    const accounts$ = action$
      .pipe(filterRequestOfType(GET_MEMBER_ALL_ACCOUNTS, 'success')).pipe(
        filter((a) => a.payload.params.type === type))

    return combineLatest(rates$, accounts$)
      .pipe(end(([ratesAction, _accountsAction]) => {
        const state = store.getState()
        const rates = ratesSelector(state, { type, timeframe: ratesAction.payload.timeframe })
        const accounts = allAccountsSelector(state, { type })
        if (!rates || !accounts) {
          return
        }

        rates.allKeys.forEach((id) => {
          const account = accounts.provided.byKey[id]
          if (!account) {
            const extra = {
              id,
              rates: getItemsFromCollection<AccountRate, 'id'>(rates),
              accounts: getItemsFromCollection<DetailedAccount, 'id'>(accounts.provided),
            }
            captureWarning(`Rate without account`, extra)
          }
        })
      }))
  }

// ===========================================================================//
//                                   TRENDS                                   //
// ===========================================================================//

const createGetMemberAccountsTrendEpic = (type: AccountType): Epic =>
  (action$, store) =>
    action$
      .pipe(byType(GET_MEMBER_ACCOUNT_TRENDS)).pipe(
        filter(({ payload }) => payload.type === type),
        distinct(
          ({ payload }) => JSON.stringify(payload),
          action$.pipe(toDiscardCache(createAccountTrendDiscardTriggers(type))),
        ),
        mergeMap(({ payload }) => {
          const { category, timeframe } = payload

          const timeperiod = createTimeperiod(timeframe)
          const scale: TimeScale = 'day'
          const state = store.getState()
          const requestPayload = {
            ...timeperiod,
            scale,
            category: resolveCategory(category, true),
            excluded_account_uids: accountsFilterQueryParamSelector(state),
          }

          const request = type === 'debts'
            ? get(GET_MEMBER_DEBTS_TREND_URL, requestPayload)
            : get(GET_MEMBER_ASSETS_TREND_URL, requestPayload)

          return request.pipe(
            takeUntil(action$.pipe(byType(PAGE_CHANGED))))
            .pipe(mapSafe(({ response: trend }) => setMemberAccountTrendsActionCreator(type, timeframe, category, trend)))
        }))

const createGetPeergroupsAccountsTrendEpic = (type: AccountType): Epic =>
  (action$) =>
    action$
      .pipe(byType(GET_PEER_GROUP_ACCOUNT_TRENDS)).pipe(
        filter(({ payload }) => payload.type === type),
        distinct(
          ({ payload }) => JSON.stringify(payload),
          action$.pipe(toDiscardCache(PEERGROUPS_DATA_DISCARD_TRIGGERS)),
        ),
        mergeMap(({ payload }) => {
          const { category, peergroups } = payload

          // request all data for 13 months, so we don't need reload data when user change timeframe
          // may be changed when custom timeframe will come
          const { start_date, end_date } = createTimeperiod('13-months')
          // also request previous month to show value on chart
          const startDateCorrected = moment(start_date).subtract(1, 'month').format(DATE_FORMAT_ISO_DAYLESS)
          const requestPayload = {
            start_date: startDateCorrected,
            end_date,
            category: resolveCategory(category, true),
            group: peergroups,
          }

          const request = type === 'debts' ?
            get(GET_PEER_GROUP_DEBTS_TREND_URL, requestPayload) :
            get(GET_PEER_GROUP_ASSETS_TREND_URL, requestPayload)

          return request.pipe(
            takeUntil(action$.pipe(byType(PAGE_CHANGED))))
            .pipe(mapSafe(({ response }) => {
              const trendsCollection = normalize(response, 'id')
              return setPeergroupAccountTrendsActionCreator(type, category, trendsCollection)
            }))
        }))


export default combineEpics(
  createGetMemberAccountsAllEpic('debts'),
  createGetMemberAccountsAllEpic('assets'),
  createGetAccountRatesEpic('debts'),
  createGetAccountRatesEpic('assets'),
  createGetMemberAccountsBreakdownEpic('debts'),
  createGetMemberAccountsBreakdownEpic('assets'),
  createGetPeergroupAccountsBreakdownEpic('debts'),
  createGetPeergroupAccountsBreakdownEpic('assets'),
  createGetMemberAccountsTrendEpic('debts'),
  createGetMemberAccountsTrendEpic('assets'),
  createGetPeergroupsAccountsTrendEpic('debts'),
  createGetPeergroupsAccountsTrendEpic('assets'),
  catchMissedRateAccountEpic('debts'),
  catchMissedRateAccountEpic('assets'),
)
